import { visit } from 'unist-util-visit';
import type { Node } from 'unist';

interface DirectiveNode extends Node {
    name?: string;
    attributes?: Record<string, string | null | undefined>;
    data?: {
        hName?: string;
        hProperties?: Record<string, unknown>;
    };
}

const KINDS: Record<string, string> = {
    info: 'Info',
    warning: 'Warning',
    error: 'Error',
};

/**
 * Turns ":::info" / ":::warning" / ":::error" container directives
 * (remark-directive) into an <alert kind="..." title="..."> hast node,
 * so the content stays regular Markdown (bold, links, lists, code, ...) and
 * no raw HTML is ever needed in the source.
 *
 * Also turns the "::anchor{id="..."}" leaf directive into an empty <span id="...">,
 * which gives in-page deep links a scroll target. Markdown headings carry no id, and
 * anchors imported from the previous knowledge base sit on paragraphs as often as on
 * headings, so a heading-slug scheme could not cover them. The "::children" leaf
 * directive becomes an <articleChildren articleId="..."> node.
 */
export default function remarkAlerts() {
    return (tree: Node) => {
        visit(tree, (node: DirectiveNode) => {
            if (node.type === 'leafDirective') {
                if (node.name === 'anchor' && node.attributes?.id) {
                    const data = node.data || (node.data = {});
                    data.hName = 'span';
                    data.hProperties = { id: node.attributes.id };
                } else if (node.name === 'children') {
                    // "::children" / "::children{id=12}": the list of an article's child articles
                    const data = node.data || (node.data = {});
                    data.hName = 'articleChildren';
                    data.hProperties = {
                        // Optional: without it the list shows the children of the article being read
                        articleId: node.attributes?.id,
                    };
                }

                return;
            }

            if (node.type !== 'containerDirective') return;

            if (node.name && KINDS[node.name]) {
                const data = node.data || (node.data = {});
                data.hName = 'alert';
                data.hProperties = {
                    kind: node.name,
                    // Title is optional: only forwarded when the author sets it.
                    title: node.attributes?.title,
                };
            } else if (node.name === 'qa') {
                const data = node.data || (node.data = {});
                data.hName = 'qa';
                data.hProperties = {
                    question: node.attributes?.question || 'Question',
                };
            }
        });
    };
}
